<script setup>
import { ref, computed } from 'vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import Badge from '@/components/Badge.vue'
import Title from '@/components/ui/Title.vue'
import CollapseContainer from '@/components/Atoms/CollapseContainer.vue'
import PlatformMetadataEditor from '@/components/ui/PlatformMetadataEditor.vue'
import ButtonCopyToClipboard from '@/components/Moleculas/ButtonCopyToClipboard.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconSettings from '@/components/icon/IconSettings.vue'
import IconPencilEdit from '@/components/icon/IconPencilEdit.vue'
import DestinationRowStatus from './DestinationRowStatus.vue'
import DestinationRowActions from './DestinationRowActions.vue'
import { usePlatformListContext } from './platformListContext'
import { coloredPlatformIcon } from '@/Utils/platformIcon'
import { getServerUrl } from '@/Utils/platformStatus'
import { formatPidsSummary, isCustomSrtDestination } from '@/Utils/srtPushTarget'

const props = defineProps({
	platform: {
		type: Object,
		required: true,
	},
	index: {
		type: Number,
		default: 0,
	},
})

const ctx = usePlatformListContext()
const metadataOpen = ref(false)
const titleRef = ref(null)

const isGuest = computed(() => !!props.platform.isGuest)
const serverUrl = computed(() => getServerUrl(props.platform))
const pidsSummary = computed(() => isCustomSrtDestination(props.platform)
	? formatPidsSummary(props.platform.pids, props.platform.pmtPid)
	: null)
const iconSrc = computed(() => coloredPlatformIcon(props.platform))
const isYoutubeEventsInvalid = computed(() =>
	!props.platform.isTokenValid && props.platform.template === 'youtube events'
)
const hasMetadata = computed(() => {
	const p = props.platform
	if (!p.serviceMeta || !p.serviceMetaEditor) return false
	return ctx.hasMetaProp('title', p) || ctx.hasMetaProp('description', p) || ctx.hasMetaProp('game', p)
})
const guestRemoved = computed(() => isGuest.value && ctx.isGuestDestinationRemoved(props.platform))
const isScheduled = computed(() => ctx.streamType === 'scheduled')
const canRename = computed(() => !props.platform.reusableDestination)
const iconBoxClass = computed(() => [
	'size-10 shrink-0 rounded-lg overflow-hidden flex items-center justify-center transition-colors',
	props.platform.enabled
		? (isGuest.value ? 'bg-surface-3 text-main' : 'bg-main-transparent text-main')
		: 'bg-surface-3',
])

function startRename() {
	titleRef.value?.editTitle()
}
</script>

<template>
	<div
		class="flex flex-row items-stretch min-w-0 w-full max-w-full transition-colors p-0 rounded-none md:rounded-lg hover:bg-surface-2 max-md:first:[&>.mobile-destination-card]:rounded-t-lg max-md:last:[&>.mobile-destination-card]:rounded-b-lg"
		:class="isScheduled ? 'md:p-0' : 'md:p-3'"
	>
		<div
			class="mobile-destination-card flex flex-row items-start md:items-stretch grow min-w-0 w-full md:p-0 gap-x-3 bg-surface-2 md:bg-transparent"
			:class="[
				ctx.streamType,
				// Scheduled: slightly less left inset so the name/url get a bit more width
				isScheduled ? 'p-2.5 md:p-0 md:ml-1' : 'p-3 md:ml-3',
				isGuest ? 'md:max-w-full' : 'md:max-w-[calc(100%-1rem)]',
			]"
		>
			<div v-if="isGuest" class="flex flex-col items-center gap-1 self-start shrink-0 w-10">
				<a
					:href="platform.serviceChannelUrl"
					target="_blank"
					:class="iconBoxClass"
				>
					<icon-base
						v-if="platform.template === 'custom'"
						class="size-5"
						fill="none"
						width="20px"
						height="20px"
						widthViewBox="24"
						heightViewBox="24"
					>
						<icon-settings />
					</icon-base>
					<img v-else-if="iconSrc" :src="iconSrc" class="size-6 object-contain">
					<i v-else class="fas fa-spinner fa-spin" />
				</a>
				<Badge size="xs" pill class="!bg-surface-3 !text-surface-8 leading-none">Guest</Badge>
			</div>
			<a
				v-else
				:href="platform.serviceChannelUrl"
				target="_blank"
				class="self-start md:self-center"
				:class="iconBoxClass"
			>
				<icon-base
					v-if="platform.template === 'custom'"
					class="size-5"
					fill="none"
					width="20px"
					height="20px"
					widthViewBox="24"
					heightViewBox="24"
				>
					<icon-settings />
				</icon-base>
				<img v-else-if="iconSrc" :src="iconSrc" class="size-6 object-contain">
				<i v-else class="fas fa-spinner fa-spin" />
			</a>

			<div class="flex flex-col grow min-w-0 max-w-full gap-y-1">
				<div class="flex flex-row flex-nowrap items-start md:items-center justify-between gap-2 w-full min-w-0">
					<div class="flex flex-col min-w-0 flex-1">
						<div class="relative flex items-center w-full min-w-0 mx-0 md:-mx-2 group/name">
							<Title
								ref="titleRef"
								class="p-0 pr-0 !mx-0 rounded-md cursor-text"
								:class="[
									canRename ? 'hover:bg-surface-3' : '',
									isScheduled ? 'md:py-1 md:px-2 md:pr-6' : 'md:p-2 md:pr-6',
								]"
								:editable="canRename"
								:title="platform.editorName"
								max-width="w-full max-w-full"
								@edit="ctx.setSelectedPlatform(platform)"
								@changed="ctx.onPlatformNameChange"
							/>
							<FormButton
								v-if="canRename"
								title="Rename"
								:custom-classes="'absolute right-1.5 top-1/2 -translate-y-1/2 opacity-0 group-hover/name:opacity-100 group-focus-within/name:!opacity-0 group-focus-within/name:pointer-events-none transition-opacity text-surface-7 hover:text-white'"
								@click.stop="startRename"
							>
								<icon-base width="13" height="13" :widthViewBox="24" :heightViewBox="24" fill="none">
									<icon-pencil-edit />
								</icon-base>
							</FormButton>
						</div>
						<span class="text-tiny text-surface-8 w-full min-w-0 max-w-full md:max-w-[400px] inline-flex items-center gap-1 group/url">
							<span class="truncate min-w-0 flex-1">{{ serverUrl }}</span>
							<ButtonCopyToClipboard
								:text="serverUrl"
								is-icon
								size="sm"
								custom-classes="hidden md:inline-flex shrink-0 opacity-0 group-hover/url:opacity-100 transition-opacity text-surface-7 hover:text-white"
							/>
						</span>
						<span v-if="pidsSummary" class="text-tiny text-surface-7 truncate" data-testid="destination-pids">
							PIDs: {{ pidsSummary }}
						</span>
					</div>
					<DestinationRowActions
						:platform="platform"
						:index="index"
						:metadata-open="metadataOpen"
						:has-metadata="hasMetadata"
						@toggle-metadata="metadataOpen = !metadataOpen"
						@open-metadata="metadataOpen = true"
					/>
				</div>
				<div v-if="guestRemoved" class="text-danger text-[12px]">
					Destination no longer available. Please contact the guest to re-add it.
				</div>
				<div
					class="flex items-center gap-2 w-full min-w-0"
					:class="isScheduled ? 'justify-between' : ''"
				>
					<DestinationRowStatus :platform="platform" />
					<!-- Pre-recorded sidebar is narrow: keep metadata off the crowded top row -->
					<FormButton
						v-if="hasMetadata && isScheduled"
						size="sm"
						type="secondary"
						class="!hidden md:!inline-flex shrink-0"
						:disabled="platform.tooltipId !== undefined || isYoutubeEventsInvalid"
						@click="metadataOpen = !metadataOpen"
					>
						{{ metadataOpen ? 'Hide metadata' : 'Show metadata' }}
					</FormButton>
				</div>
				<FormButton
					v-if="hasMetadata"
					size="sm"
					type="secondary"
					class="self-start md:hidden"
					:disabled="platform.tooltipId !== undefined || isYoutubeEventsInvalid"
					@click="metadataOpen = !metadataOpen"
				>
					{{ metadataOpen ? 'Hide metadata' : 'Show metadata' }}
				</FormButton>
				<div
					v-if="hasMetadata"
					class="w-full min-w-0 md:w-[90%] max-md:w-[calc(100%+3.25rem)] max-md:-ml-[3.25rem]"
				>
					<CollapseContainer :is-open="metadataOpen">
						<PlatformMetadataEditor
							:platform="platform"
							:index="index"
							@meta-change="(p) => ctx.onMetaPropChange(p, platform)"
							@save="() => ctx.saveAllMeta(platform)"
							@save-all="() => ctx.requestMetaToAllPlatforms(platform)"
							@game-input="(e) => ctx.onChangeGameName(e, platform, index)"
						/>
					</CollapseContainer>
				</div>
			</div>
		</div>
	</div>
</template>
